<template>
  <div class="login-register-header-section wk-header-container">
    <ul class="wk-nav" role="menu">
      <li v-if="showRegisterButton" role="menuitem">
        <div class="login-register-item">
          <utd-button ref="registerBtn"
                      class="register-btn"
                      button-type="link"
                      :button-link="registerHref"
                      button-size="small"
                      button-color="green-shade-3">{{ registerButtonText }}</utd-button>
        </div>
      </li>
      <li v-if="showLoginButton" role="menuitem">
        <div class="login-register-item">
          <utd-button ref="loginBtn"
                      class="login-btn"
                      button-type="link"
                      :button-link="'/login'"
                      button-size="small"
                      button-color="blue"
                      @click="onLoginClick">{{ loginButtonText }}</utd-button>
        </div>
      </li>
    </ul>
    <!-- eslint-disable vue/no-v-html -->
    <button v-if="isDesktopView"
            ref="registerTooltip"
            class="wk-header__register-tooltip"
            @click="onLoginClick"
            v-html="$t('LOGIN.REGISTER_FOR_AN_ACCOUNT')" />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdCache from '_acaSrc/utility/UtdCache';
import { RichContentTooltip } from '_acaSrc/utility/tooltip';
import Logger from '_acaSrc/utility/Logger';
import { C_UI } from '_acaSrc/utility/constants';
import { SET_IS_EXPERT_AI_REDIRECT } from '_acaSrc/store/login.store';

export default {
    components: { UtdButton },
    computed: {
        ...mapGetters('app', [ 'routeLoading', 'tooltipConfig' ]),
        ...mapGetters('user', [ 'userLoggedIn' ]),
        ...mapGetters('login', [ 'loginRegisterButton', 'loginHref' ]),
        ...mapGetters('feature', [ 'isHomepageRedesign2024' ]),
        ...mapGetters('device', [ 'isDesktopView' ]),
        registerButtonText() {
            return this.$t('LOGIN.REGISTER');
        },
        loginButtonText() {
            return this.$t('MISC.LOG_IN');
        },
        registerHref() {
            return `${this.loginHref}?source=header`;
        },
        isLoggedIn() {
            return this.userLoggedIn;
        },
        showRegisterButton() {
            return this.loginRegisterButton
                && !this.isLoggedIn;
        },
        showLoginButton() {
            return !this.isLoggedIn && this.routeLoading !== 'login';
        }
    },
    mounted() {
        this.setupTooltips();
    },
    methods: {
        ...mapActions('app', [ 'confirmConfigLoaded' ]),
        ...mapMutations('login', [ SET_IS_EXPERT_AI_REDIRECT ]),
        onLoginClick() {
            // save URL that the user is currently at in order to redirect them here after SSO login
            const relativeUrl = window.location.pathname + window.location.search;
            const cache = new UtdCache();
            cache.setPersistent('isExpertAiRedirect', false, 1);
            // we don't want to redirect the user to the login page if they are already there
            if (relativeUrl !== '/login') {
                cache.setPersistent('ssoRedirectURL', relativeUrl, 1);
            }
        },
        async setupTooltips() {
            const registerBtn = this.$refs.registerBtn?.$el;
            const registerTooltipEl = this.$refs.registerTooltip;
            if (registerBtn && registerTooltipEl) {
                await this.confirmConfigLoaded();
                const registerTooltip = new RichContentTooltip(registerBtn, this.tooltipConfig, {
                    trigger: 'mouseenter focusin',
                    allowHTML: true,
                    content: registerTooltipEl,
                    appendTo: registerBtn,
                    offset: [ 50, 10 ],
                    delay: C_UI.MENU_SHOW_DELAY_MS
                });
                registerTooltip.initialize().catch(e => {
                    Logger.warn('Error occurred during tooltip initialization:');
                    Logger.warn(e);
                });
            }
        }
    }
};
</script>

<style lang="less" scoped>
.utdWkHomePage2024 #loginRegisterButtons .wk-nav {
  display: block;
}

.login-register-header-section > .wk-header__register-tooltip {
  display: none; // hide before tooltip init
}
</style>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles.less';
@import (reference) '~_acaAssets/utd-button/utd-button';
@import (reference) '~_acaAssets/wkce/mixins.less';

.login-register-header-section.wk-header-container .wk-nav,
.utdWkHomePage2024  .wk-navbar .login-register-header-section .wk-nav > li {
  a.utd-button.utd-button-link {
    margin-right: 24px;
    font-size: 14px;
    font-weight: 500;
    color: @DS1-LIGHT-ON-DARK-FONT-COLOR;
  }
}

.login-register-header-section.wk-header-container .wk-nav {
  display: block;
}

.wk-header__register-tooltip {
  .wk-header-tooltip();
  max-width: 180px;
}

@media (max-width: 767px) {
  .utdWkHeader .wk-navbar .wk-navbar-container {
    .mobileonDesktop&,
    .isSmallScreen&,
    .isTablet & {
      .login-register-header-section .wk-nav li {
        float: left;

        &:nth-child(1) {
          margin: 12px 6px 12px 12px;
        }

        &:nth-child(2) {
          margin: 12px 12px 12px 6px;
        }

        a.utd-button.utd-button-link {
          margin-right: unset;
          padding: 7px 16px;
          border-width: 1px;

          &.utd-button-color-green-shade-3 {
            .utd-button-color-green-shade-3-style();
          }
        }
      }
    }
  }
}

@media only screen and (min-width: 768px) {
  .isDesktop {
    .utdDeskBanner {
      &.navOverflowed {
        .login-register-header-section {
          display: block;

          .fixedToolbar& {
            visibility: hidden;
          }
        }
      }
    }

    #utdNavRoot {
      .login-register-header-section {
        display: none;
        position: absolute;
        right: 0;
      }

      &.navOverflowed .login-register-header-section {
          visibility: hidden;
      }
    }
  }
}

@media only screen and (min-width: 812px) {
  .isDesktop.isHomeSearchEmpty.anonymous-user {
    .utdDeskBanner .login-register-header-section {
      display: inline-block;
      
  
    }
  }
}

@media only screen and (min-width: 1126px) {
  .isDesktop.utdWkHomePage2024.anonymous-user:not(.isHomeSearchEmpty) {
    .utdDeskBanner .login-register-header-section {
      display: inline-block;
    }
  }
}

@media (min-width: 37.5rem) {
  .wk-navbar .wk-nav > li > a.utd-button.utd-button-link {
    border-width: 1px;
  }
}
</style>
